import Slider from '@mui/material/Slider'
import { useEffect, useState } from 'react'
import GoogleMap from '../../../../Components/GoogleMap/GoogleMap'
import LocationSearchBox, { LocationSearchBoxProps } from './SearchBox/SearchBox'
import { LocationAttributes } from '../../../../Types/Location'
import { fetchLocationByPlaceId, getCurrencyByCountry } from '../../../../Utilities/Location'
import { PartialBrief } from '../../../../Types/Brief'
import { GoogleLocationAttributes } from '../../../../Utilities/Location/fetchLocationByCoordinates'
import { Input } from '../../../../Components/Form/Inputs'
import useBriefContext from '../../../../Hooks/Brief/useBriefContext'

export const getDefaultLocationAttrs = (
  brief: PartialBrief
): Omit<LocationAttributes, 'radius'> | undefined => {
  const { lat, lon, country, leadFeedCity, location, currency } = brief
  if (lat !== undefined && lon !== undefined && location !== undefined) {
    return {
      lat,
      lon,
      country,
      leadFeedCity,
      location,
      currency: currency ?? getCurrencyByCountry(country),
    }
  }
  return undefined
}

export type LocationAttributesWithoutRadius = Omit<LocationAttributes, 'radius'>

interface LocationInputProps {
  radius: number
  locationAttributes?: LocationAttributesWithoutRadius
  setLocation: (location: LocationAttributesWithoutRadius | undefined) => void
  setRadius: (radius: LocationAttributes['radius']) => void
}

const LocationInput: (props: LocationInputProps) => JSX.Element = ({
  radius,
  setLocation: _setLocation,
  setRadius,
  locationAttributes,
}) => {
  const { loading, brief } = useBriefContext()
  const [map, setMap] = useState<google.maps.Map>()
  const [placeId, setPlaceId] = useState<string>()
  const [placesService, setPlacesService] = useState<google.maps.places.PlacesService>()
  const center = locationAttributes && {
    lat: locationAttributes.lat,
    lon: locationAttributes.lon,
  }
  const setLocation = (googleLocation?: GoogleLocationAttributes) => {
    const currency = getCurrencyByCountry(googleLocation?.country)
    const newLocationAttrs = googleLocation && {
      ...googleLocation,
      currency,
    }
    _setLocation(newLocationAttrs)
  }

  useEffect(() => {
    map && setPlacesService(new google.maps.places.PlacesService(map))
  }, [map])

  useEffect(() => {
    if (!placeId || !placesService) return

    const fetchAndSetLocation = async () => {
      const fetchedLocationAttrs = await fetchLocationByPlaceId({ placeId, placesService })
      setLocation(fetchedLocationAttrs)
    }

    fetchAndSetLocation()
  }, [placeId])

  useEffect(() => {
    if (loading) return

    setLocation(getDefaultLocationAttrs(brief))
  }, [loading])

  const handleLocationSelect: LocationSearchBoxProps['setLocation'] = (value) => {
    setPlaceId(value)
  }

  const onSliderChange = (_event: Event, newValue: number | number[]) => {
    setRadius(Array.isArray(newValue) ? newValue[0] : newValue)
  }

  const onNumInputChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    setRadius(target.value === '' ? 0 : Number(target.value))
  }

  return (
    <>
      <LocationSearchBox
        id='location-search'
        location={locationAttributes?.location ?? ''}
        setLocation={handleLocationSelect}
      />
      <GoogleMap
        radiusKM={radius}
        center={center && { lat: center.lat, lon: center.lon }}
        setMapInParent={setMap}
        show={!!locationAttributes?.location}
      />
      <div className={`combined-slider ${locationAttributes?.location ? 'show' : ''}`}>
        <Slider
          className='location-slider'
          min={0.5}
          max={25}
          step={0.5}
          value={radius}
          onChange={onSliderChange}
          valueLabelDisplay='auto'
          valueLabelFormat={(val) => `${val}km`}
          aria-hidden='true'
          sx={{ root: { color: '#AF231C' } }}
          tabIndex={-1}
        />
        <label htmlFor='radius-number-input' className='visually-hidden'>
          Radius Number Input
        </label>
        <Input
          id='radius-number-input'
          type='number'
          min={0.5}
          max={25}
          step={0.5}
          value={radius}
          aria-valuetext={`${radius} kilometres`}
          onChange={onNumInputChange}
        />
        <span aria-hidden='true'>km</span>
      </div>
    </>
  )
}

export default LocationInput
